import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col, Spinner, Alert } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import apiClient from "../../api/apiClient";
import { toast } from "react-toastify";
import { Role, User } from "../../features/users/usersSlice";
import { Company } from "../../features/companies/companiesService";
import usersService from "../../features/users/usersService";

interface UserFormModalProps {
  show: boolean;
  onHide: (refresh?: boolean) => void;
  user: User | null;
  roles: Role[];
  companies: Company[];
  agencyId?: number;
}

interface UserFormData {
  username: string;
  email: string;
  full_name: string;
  password?: string;
  status: string;
  role_ids: string[];
  company_ids: number[];
}

const UserFormModal: React.FC<UserFormModalProps> = ({
  show,
  onHide,
  user,
  roles,
  companies,
  agencyId,
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    username: "",
    email: "",
    full_name: "",
    password: "",
    status: "active",
    role_ids: [],
    company_ids: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(true);
  const [selectedCompanies, setSelectedCompanies] = useState<
    { id: number; role?: string }[]
  >([]);

  // Kullanıcı bilgilerini al
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  const currentUserRoles = currentUser?.roles || [];
  const isAgencyOwner = currentUserRoles.includes('agency_owner');
  const isAdmin = currentUserRoles.includes('admin') || currentUserRoles.includes('superadmin');
  const isCompanyOwner = currentUserRoles.includes('company_owner');

  const isEditMode = !!user;

  // Kullanıcı değiştiğinde formu güncelle
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username,
        email: user.email,
        full_name: user.full_name,
        status: user.status,
        role_ids: user.roles, // Roller artık string olduğundan, doğrudan kullanıyoruz
        company_ids: user.companies.map((company) => company.company_id),
      });

      setSelectedCompanies(
        user.companies.map((company) => ({
          id: company.company_id,
          role: company.role,
        }))
      );

      setPasswordRequired(false);
    } else {
      // Yeni kullanıcı için formu sıfırla
      setFormData({
        username: "",
        email: "",
        full_name: "",
        password: "",
        status: "active",
        role_ids: [],
        company_ids: [],
      });

      setSelectedCompanies([]);
      setPasswordRequired(true);
    }

    setError(null);
  }, [user, show]);

  // Agency Owner için otomatik rol ve şirket seçimi
  useEffect(() => {
    if (!user && show && roles.length > 0 && companies.length > 0 && isAgencyOwner) {
      // Sadece Agency Owner için otomatik olarak marketing_specialist rolü ve agency'si seç
      const marketingSpecialistRole = roles.find(role => role.key === "marketing_specialist");
      const agency = companies.find(company => company.company_type === "agency");
      
      if (marketingSpecialistRole && agency) {
        setFormData(prev => ({
          ...prev,
          role_ids: [marketingSpecialistRole.key]
        }));
        
        setSelectedCompanies([{ id: agency.id, role: "member" }]);
      }
    }
  }, [user, show, roles, companies, isAgencyOwner]);

  // Company Owner için otomatik rol ve tek şirket seçimi
  useEffect(() => {
    if (!user && show && roles.length > 0 && companies.length === 1 && !isAgencyOwner && !isAdmin) {
      const companySpecialistRole = roles.find(role => role.key === "company_specialist");
      if (companySpecialistRole) {
        setFormData(prev => ({
          ...prev,
          role_ids: [companySpecialistRole.key]
        }));
      }
      setSelectedCompanies([{ id: companies[0].id, role: "member" }]);
    }
  }, [user, show, roles, companies, isAgencyOwner, isAdmin]);

  // Form alanlarını güncelle
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Rol seçimini güncelle
  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const roleId = e.target.value;
    const isChecked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      role_ids: isChecked
        ? [...prev.role_ids, roleId]
        : prev.role_ids.filter((id) => id !== roleId),
    }));
  };

  // Şirket seçimini güncelle
  const handleCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const companyId = Number(e.target.value);
    if (companyId && !selectedCompanies.some((c) => c.id === companyId)) {
      setSelectedCompanies([...selectedCompanies, { id: companyId }]);
    }
  };

  // Şirket rolünü güncelle
  const handleCompanyRoleChange = (companyId: number, role: string) => {
    setSelectedCompanies((prev) =>
      prev.map((company) =>
        company.id === companyId ? { ...company, role } : company
      )
    );
  };

  // Şirketi kaldır
  const handleRemoveCompany = (companyId: number) => {
    setSelectedCompanies((prev) =>
      prev.filter((company) => company.id !== companyId)
    );
  };

  // Formu gönder
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let updatedFormData;

      if (isEditMode) {
        // Edit modunda şirket seçimlerini kullan
        const updateUserData: any = {
          username: formData.username,
          email: formData.email,
          full_name: formData.full_name,
          status: formData.status,
          role_ids: formData.role_ids,
          company_ids: selectedCompanies.map((company) => company.id),
        };

        // Şifre sadece girilmişse ekle
        if (formData.password) {
          updateUserData.password = formData.password;
        }

        // Agency ID varsa yeni API'yi kullan
        if (agencyId && user) {
          await usersService.updateAgencyUser(agencyId, user.id, updateUserData);
        } else {
          // Kullanıcıyı güncelle
          await apiClient.put(`/users/${user.id}`, updateUserData);
        }
        toast.success("User updated successfully");
      } else {
        // Yeni kullanıcı oluştur
        let createUserData: any = {
          username: formData.username,
          email: formData.email,
          full_name: formData.full_name,
          password: formData.password,
          status: formData.status,
          role_ids: formData.role_ids,
        };

        // Agency Owner için otomatik şirket ataması, Admin/Superadmin için kullanıcının seçtiği şirketler
        if (isAgencyOwner) {
          const agency = companies.find(company => company.company_type === "agency");
          createUserData.company_ids = agency ? [agency.id] : [];
        } else {
          // Admin/Superadmin için seçilen şirketleri kullan
          createUserData.company_ids = selectedCompanies.map(company => company.id);
        }

        // Yeni kullanıcı oluştur
        await apiClient.post("/users", createUserData);
        const successMessage = isAgencyOwner ? "Marketing Specialist created successfully" : "User created successfully";
        toast.success(successMessage);
      }

      onHide(true); // Modal'ı kapat ve listeyi yenile
    } catch (error: any) {
      console.error("Error saving user:", error);
      setError(error.response?.data?.detail || "Failed to save user");
      toast.error("Failed to save user");
    } finally {
      setLoading(false);
    }
  };

  // Şirket adını ID'ye göre bul
  const getCompanyName = (companyId: number) => {
    // Önce selectedUser'daki companies'den company_name'i kontrol et
    if (user && user.companies) {
      const userCompany = user.companies.find((c: any) => c.company_id === companyId);
      if (userCompany && userCompany.company_name) {
        return userCompany.company_name;
      }
    }
    
    // Eğer company_name yoksa companies array'inden bul
    const company = companies.find((c) => c.id === companyId);
    return company ? company.company_name : `Company ${companyId}`;
  };

  return (
    <Modal show={show} onHide={() => onHide()} size="lg" centered className="user-form-modal">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode
              ? "Edit User"
              : (isAgencyOwner
                  ? "Add Marketing Specialist"
                  : (!isAdmin && companies.length === 1
                      ? "Add Company Specialist"
                      : "Add User"))}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Username *</Form.Label>
                <Form.Control
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={isEditMode}
                  placeholder="Enter username"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email *</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Enter email address"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Enter full name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {!isEditMode && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password *</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={passwordRequired}
                    placeholder="Enter password"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password *</Form.Label>
                  <Form.Control
                    type="password"
                    name="confirmPassword"
                    onChange={handleChange}
                    required={passwordRequired}
                    placeholder="Confirm password"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          {isEditMode && (
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>New Password (leave blank to keep current)</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter new password"
                  />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Roles *</Form.Label>
            <div>
              {(isAdmin
                ? [
                    ...roles,
                    ...(!roles.some(r => r.key === 'company_owner')
                      ? [{ id: -1001 as unknown as number, key: 'company_owner', name: 'Company Owner' } as Role]
                      : []),
                    ...(!roles.some(r => r.key === 'company_specialist')
                      ? [{ id: -1002 as unknown as number, key: 'company_specialist', name: 'Company Specialist' } as Role]
                      : []),
                  ]
                : roles).map((role) => (
                <Form.Check
                  key={role.key}
                  type="checkbox"
                  id={`role-${role.key}`}
                  label={role.name}
                  value={role.key}
                  checked={formData.role_ids.includes(role.key)}
                  onChange={handleRoleChange}
                  disabled={!isEditMode && isAgencyOwner && role.key === "marketing_specialist"}
                  className="role-checkbox"
                />
              ))}
            </div>
            {!isEditMode && isAgencyOwner && (
              <Form.Text className="text-muted">
                Marketing Specialist role is automatically selected for new team members.
              </Form.Text>
            )}
            {isEditMode && (
              <Form.Text className="text-muted">
                Select the roles for this user. Agency Owner can manage the agency, Marketing Specialist can work on client projects.
              </Form.Text>
            )}
          </Form.Group>

          {/* Şirket seçimi edit modunda veya admin/superadmin için göster */}
          {(isEditMode || (!isAgencyOwner && !isCompanyOwner)) && (
            <Form.Group className="mb-3">
              <Form.Label>Assign to Companies</Form.Label>
              <Form.Select
                value=""
                onChange={handleCompanyChange}
              >
                <option value="">Select a company...</option>
                <optgroup label="Agency">
                  {companies
                    .filter((company) => company.company_type === "agency")
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Client Companies">
                  {companies
                    .filter((company) => company.company_type === "client")
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                </optgroup>
                <optgroup label="Single Companies">
                  {companies
                    .filter((company) => company.company_type === "single")
                    .map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.company_name}
                      </option>
                    ))}
                </optgroup>
              </Form.Select>
              
              {selectedCompanies.length > 0 && (
                <div className="mt-2">
                  <h6>Selected Companies:</h6>
                  {selectedCompanies.map((company) => (
                    <div key={company.id} className="d-flex align-items-center mb-2 p-2 border rounded">
                      <div className="flex-grow-1">
                        <strong>{getCompanyName(company.id)}</strong>
                        <div className="text-muted small">
                          {(() => {
                            const type = companies.find(c => c.id === company.id)?.company_type;
                            if (type === "agency") return "Agency";
                            if (type === "client") return "Client Company";
                            if (type === "single") return "Single Company";
                            return "Company";
                          })()}
                        </div>
                      </div>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemoveCompany(company.id)}
                        className="ms-2"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          )}

          {/* Yeni kullanıcı için bilgi mesajı */}
          {!isEditMode && isAgencyOwner && (
            <Alert variant="info" className="mt-3">
              <strong>Note:</strong> New marketing specialists will be automatically assigned to your agency with the Marketing Specialist role.
            </Alert>
          )}
          {!isEditMode && !isAgencyOwner && !isCompanyOwner && (
            <Alert variant="info" className="mt-3">
              <strong>Note:</strong> Please select the companies where this user will work and assign appropriate roles.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => onHide()}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              isEditMode
                ? "Update User"
                : (isAgencyOwner
                    ? "Create Marketing Specialist"
                    : (!isAdmin && companies.length === 1
                        ? "Create Company Specialist"
                        : "Create User"))
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserFormModal;

// CSS Styles
const styles = `
  /* Theme tokens */
  .user-form-modal {
    --panel-bg: #ffffff;
    --body-bg: #f8fafc;
    --border-color: #e5e7eb;
    --text-primary: #0f172a;
    --text-muted: #64748b;
    --control-bg: #f9fafb;
    --shadow: 0 20px 60px rgba(0,0,0,0.15);
  }
  [data-bs-theme="dark"] .user-form-modal {
    --panel-bg: #0b1220;
    --body-bg: #0f172a;
    --border-color: #334155;
    --text-primary: #f8fafc;
    --text-muted: #94a3b8;
    --control-bg: #111827;
    --shadow: 0 24px 60px rgba(0,0,0,0.45);
  }

  .user-form-modal .modal-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-bottom: none;
  }

  .user-form-modal .modal-content {
    background: var(--panel-bg);
    border: 1px solid var(--border-color);
    border-radius: 16px;
    box-shadow: var(--shadow);
  }

  .user-form-modal .modal-body {
    background: var(--body-bg);
  }

  .user-form-modal .modal-title {
    font-weight: 600;
    font-size: 1.25rem;
  }

  .user-form-modal .form-label {
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 0.5rem;
  }

  .user-form-modal .form-control,
  .user-form-modal .form-select {
    border-radius: 8px;
    border: 1px solid var(--border-color);
    padding: 0.75rem;
    font-size: 0.9rem;
    transition: all 0.3s ease;
    background: var(--control-bg);
    color: var(--text-primary);
  }

  .user-form-modal .form-control:focus,
  .user-form-modal .form-select:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
    background: var(--panel-bg);
  }

  .user-form-modal .form-check {
    margin-bottom: 0.5rem;
  }

  .user-form-modal .form-check-input:checked {
    background-color: #667eea;
    border-color: #667eea;
  }

  .user-form-modal .form-check-label {
    color: var(--text-primary);
    font-weight: 500;
    cursor: pointer;
  }

  .user-form-modal .role-checkbox .form-check-label {
    color: var(--text-primary);
    font-weight: 500;
    font-size: 0.9rem;
  }

  .user-form-modal .role-checkbox .form-check-input {
    margin-right: 8px;
  }

  .user-form-modal .role-checkbox .form-check-input:checked {
    background-color: #667eea;
    border-color: #667eea;
  }

  .user-form-modal .role-checkbox .form-check-input:focus {
    border-color: #667eea;
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
  }

  /* Improve checkbox contrast on light/dark backgrounds */
  .user-form-modal .form-check-input {
    background-color: var(--control-bg);
    border: 2px solid var(--border-color);
  }
  .user-form-modal .form-check-input:hover {
    border-color: #94a3b8;
  }
  .user-form-modal .form-check-input:checked {
    background-color: #4f46e5; /* indigo-600 */
    border-color: #4f46e5;
  }

  .user-form-modal .alert {
    border-radius: 8px;
    border: none;
  }

  .user-form-modal .modal-footer {
    background: var(--panel-bg);
    border-top: 1px solid var(--border-color);
  }

  .user-form-modal .btn {
    border-radius: 8px;
    padding: 0.5rem 1.5rem;
    font-weight: 600;
    transition: all 0.3s ease;
  }

  .user-form-modal .btn-primary {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
  }

  .user-form-modal .btn-primary:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  .user-form-modal .btn-secondary {
    background: #6c757d;
    border: none;
  }

  .user-form-modal .btn-secondary:hover {
    background: #5a6268;
  }

  /* Selected companies list item styling */
  .user-form-modal .border.rounded {
    background: var(--control-bg);
    border-color: var(--border-color) !important;
  }
  .user-form-modal .border.rounded .text-muted.small {
    color: var(--text-muted) !important;
  }
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}
